/**
 * useChartZoomWindow : zoom des graphiques de la page de session et lecture de
 * la vue zoomée (« Analyser la vue zoomée », motif D223 de la comparaison).
 *
 * Les graphiques de session ont un axe catégoriel (une étiquette HH:MM par
 * mesure) : les bornes de la vue zoomée sont des index, convertis ici en
 * horodatages des mesures (ms epoch).
 *
 * Pourquoi un abonnement plutôt qu'un état du graphique : react-chartjs-2
 * réapplique les options à chaque rendu du composant qui porte le graphique,
 * ce qui effacerait le zoom en cours. Seul le bouton se rend à nouveau quand la
 * vue change ; le graphique, lui, ne bouge pas.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const toMs = (date) => new Date(date).getTime();

/** Fenêtre { start, end } (ms) de la vue d'un axe catégoriel, ou null. */
export function zoomedWindow(readings, scale) {
  const list = readings || [];
  if (!list.length || !scale || !Number.isFinite(scale.min) || !Number.isFinite(scale.max)) return null;
  const clamp = (i) => Math.min(list.length - 1, Math.max(0, i));
  const start = toMs(list[clamp(Math.floor(scale.min))]?.timestamp);
  const end = toMs(list[clamp(Math.ceil(scale.max))]?.timestamp);
  return Number.isFinite(start) && Number.isFinite(end) && start < end ? { start, end } : null;
}

/**
 * @param {Object} params
 * @param {Array} params.readings — mesures affichées, dans l'ordre de l'axe
 * @param {(start:number, end:number) => void} [params.onAnalyzeZoom]
 * @param {string} [params.panModifierKey] — touche du panoramique ('shift'), aucune si absente
 * @param {number} [params.wheelSpeed] — vitesse de la molette, défaut Chart.js si absente
 * @param {boolean} [params.limitToData] — zoom borné aux données d'origine
 * @returns {{ chartRef, zoomOptions, resetZoom, analyzeZoom, subscribe, canAnalyze: boolean }}
 */
export default function useChartZoomWindow({
  readings, onAnalyzeZoom, panModifierKey, wheelSpeed, limitToData = false,
}) {
  const chartRef = useRef(null);
  const listeners = useRef(new Set());
  const notify = useCallback((zoomed) => listeners.current.forEach(fn => fn(zoomed)), []);
  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  const zoomOptions = useMemo(() => {
    const markZoomed = () => notify(true);
    return {
      pan: { enabled: true, mode: 'x', ...(panModifierKey ? { modifierKey: panModifierKey } : {}), onPanComplete: markZoomed },
      zoom: {
        wheel: { enabled: true, ...(wheelSpeed ? { speed: wheelSpeed } : {}) },
        pinch: { enabled: true },
        mode: 'x',
        onZoomComplete: markZoomed,
      },
      ...(limitToData ? {
        limits: { x: { min: 'original', max: 'original' }, y: { min: 'original', max: 'original' } },
      } : {}),
    };
  }, [notify, panModifierKey, wheelSpeed, limitToData]);

  const resetZoom = useCallback(() => {
    chartRef.current?.resetZoom();
    notify(false);
  }, [notify]);

  const analyzeZoom = useCallback(() => {
    const window = zoomedWindow(readings, chartRef.current?.scales?.x);
    if (window && onAnalyzeZoom) onAnalyzeZoom(window.start, window.end);
  }, [readings, onAnalyzeZoom]);

  return { chartRef, zoomOptions, resetZoom, analyzeZoom, subscribe, canAnalyze: Boolean(onAnalyzeZoom) };
}

/** Vue zoomée ou non, pour le seul composant abonné (le bouton). */
export function useZoomed(subscribe) {
  const [zoomed, setZoomed] = useState(false);
  useEffect(() => subscribe(setZoomed), [subscribe]);
  return zoomed;
}
